import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import type { OptionsMap } from '@openx/types/options.ts';
import type { CriteriaObjectOa } from '@openx/types/package.ts';
import { TechnologyAndDevicesOption } from '@openx/types/targeting/technologyAndDevices.ts';
import { useMemo } from 'react';
import { usePackageContext } from '../../../utils/context/PackageContext.tsx';
import { mapPackages } from '../utils/mapPackages.ts';

export const usePackagesList = (criteriaObjectOa?: CriteriaObjectOa, options?: { fetchRelatedAccounts?: boolean }) => {
  const {
    usePackagesListFetch,
    useRelatedAccountsFetch,
    useDeviceTypesOptionsFetch,
    useDistributionChannelOptions,
    useOldDistributionChannelOptions,
    useTechnologyOptionsFetch,
    isOa,
  } = usePackageContext();

  const { useAdPlacementOptions, useOldAdPlacementOptions } = useTargetingContext();

  const { criteria, loading, packagesList } = usePackagesListFetch(criteriaObjectOa);

  const { relatedAccounts } = useRelatedAccountsFetch(packagesList, {
    enabled: options?.fetchRelatedAccounts ?? false,
  });

  const { desktop_devices, mobile_devices, tv_devices } = useDeviceTypesOptionsFetch();

  const { distributionChannel, isDistributionChannelLoading } = useDistributionChannelOptions();

  const { deviceTypeOptions: oldDeviceTypeOptions } = useTechnologyOptionsFetch(
    TechnologyAndDevicesOption.DEVICE_TYPE,
    true
  );

  const { distributionChannel: oldDistributionChannel } = useOldDistributionChannelOptions();

  const { adPlacement: currentAdPlacement, isAdPlacementLoading } = useAdPlacementOptions();

  const { adPlacement: oldAdPlacement, isAdPlacementLoading: isOldAdPlacementLoading } = useOldAdPlacementOptions();

  const deviceTypes: OptionsMap = useMemo(
    () => ({
      ...desktop_devices.data,
      ...mobile_devices.data,
      ...tv_devices.data,
    }),
    [desktop_devices.data, mobile_devices.data, tv_devices.data]
  );

  const distributionChannelOptions: OptionsMap = useMemo(
    () => ({
      ...oldDistributionChannel,
      ...distributionChannel,
    }),
    [distributionChannel, oldDistributionChannel]
  );

  const deviceTypeDisplayOptions: OptionsMap = useMemo(
    () => ({
      ...oldDeviceTypeOptions,
      ...deviceTypes,
    }),
    [deviceTypes, oldDeviceTypeOptions]
  );

  const adPlacementOptions: OptionsMap = useMemo(
    () => ({
      ...oldAdPlacement,
      ...currentAdPlacement,
    }),
    [currentAdPlacement, oldAdPlacement]
  );

  const mappedPackagesList = useMemo(
    () =>
      mapPackages({
        packagesList,
        deviceTypeOptions: deviceTypeDisplayOptions,
        distributionChannelOptions,
        adPlacementOptions,
        isOa,
      }),
    [adPlacementOptions, deviceTypeDisplayOptions, distributionChannelOptions, isOa, packagesList]
  );

  return {
    adPlacementOptions,
    criteria,
    deviceTypeOptions: deviceTypes,
    distributionChannelOptions,
    isAdPlacementLoading: isAdPlacementLoading || isOldAdPlacementLoading,
    isDeviceTypeLoading: desktop_devices.isLoading || mobile_devices.isLoading || tv_devices.isLoading,
    isDistributionChannelLoading,
    loading,
    mappedPackagesList,
    relatedAccounts,
  };
};
